<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import CompanyAssignmentsAPI from 'dashboard/api/companyAssignments';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CompanySelect from 'dashboard/components-next/Companies/CompanySelect.vue';

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');

const assignments = ref([]);
const removedIds = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);

const targetOptions = computed(() => [
  { label: t('COMPANY_ASSIGNMENTS.TARGET_AGENT'), value: 'agent' },
  { label: t('COMPANY_ASSIGNMENTS.TARGET_TEAM'), value: 'team' },
]);

const assigneeOptions = target => {
  const list = target === 'team' ? teams.value : agents.value;
  return (list || []).map(item => ({ label: item.name, value: item.id }));
};

const fetchAssignments = async () => {
  isFetching.value = true;
  try {
    const { data } = await CompanyAssignmentsAPI.get();
    assignments.value = data.map(item => ({
      id: item.id,
      companyId: item.company_id,
      companyName: item.company_name,
      target: item.team_id ? 'team' : 'agent',
      assigneeId: item.team_id || item.agent_id,
    }));
  } catch (error) {
    useAlert(t('COMPANY_ASSIGNMENTS.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const addRow = () => {
  assignments.value.push({
    id: null,
    companyId: null,
    companyName: '',
    target: 'agent',
    assigneeId: null,
  });
};

const removeRow = index => {
  const { id } = assignments.value[index];
  if (id) removedIds.value.push(id);
  assignments.value.splice(index, 1);
};

const onTargetChange = (assignment, target) => {
  assignment.target = target;
  assignment.assigneeId = null;
};

const toPayload = assignment => ({
  company_assignment: {
    company_id: assignment.companyId,
    agent_id: assignment.target === 'agent' ? assignment.assigneeId : null,
    team_id: assignment.target === 'team' ? assignment.assigneeId : null,
  },
});

const validate = () => {
  if (assignments.value.some(a => !a.companyId || !a.assigneeId)) {
    return t('COMPANY_ASSIGNMENTS.VALIDATION.REQUIRED');
  }
  const companyIds = assignments.value.map(a => a.companyId);
  if (new Set(companyIds).size !== companyIds.length) {
    return t('COMPANY_ASSIGNMENTS.VALIDATION.DUPLICATE');
  }
  return null;
};

const saveAssignments = async () => {
  const validationError = validate();
  if (validationError) {
    useAlert(validationError);
    return;
  }

  isSaving.value = true;
  try {
    // Deletes first so a company removed and re-added doesn't hit the unique index
    await Promise.all(
      removedIds.value.map(id => CompanyAssignmentsAPI.delete(id))
    );
    await Promise.all(
      assignments.value.map(assignment =>
        assignment.id
          ? CompanyAssignmentsAPI.update(assignment.id, toPayload(assignment))
          : CompanyAssignmentsAPI.create(toPayload(assignment))
      )
    );
    removedIds.value = [];
    await fetchAssignments();
    useAlert(t('COMPANY_ASSIGNMENTS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('COMPANY_ASSIGNMENTS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  fetchAssignments();
});
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex items-center justify-end gap-2 px-4 pt-4">
      <Button
        :label="t('COMPANY_ASSIGNMENTS.ADD_ROW')"
        icon="i-lucide-plus"
        variant="outline"
        color="slate"
        size="sm"
        @click="addRow"
      />
      <Button
        :label="t('COMPANY_ASSIGNMENTS.SAVE')"
        icon="i-lucide-save"
        size="sm"
        :is-loading="isSaving"
        @click="saveAssignments"
      />
    </div>

    <div class="flex flex-col gap-4 p-4">
      <p class="text-sm text-n-slate-11">
        {{ t('COMPANY_ASSIGNMENTS.DESCRIPTION') }}
      </p>

      <div
        v-if="isFetching"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner />
        <span>{{ t('COMPANY_ASSIGNMENTS.LOADING') }}</span>
      </div>

      <div
        v-else-if="!assignments.length"
        class="p-4 text-sm border border-dashed rounded-md border-n-weak text-n-slate-11"
      >
        {{ t('COMPANY_ASSIGNMENTS.EMPTY_STATE') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          class="hidden md:grid grid-cols-[260px_200px_260px_auto] gap-3 text-xs font-semibold uppercase text-n-slate-11"
        >
          <span>{{ t('COMPANY_ASSIGNMENTS.COMPANY_LABEL') }}</span>
          <span>{{ t('COMPANY_ASSIGNMENTS.TARGET_LABEL') }}</span>
          <span>{{ t('COMPANY_ASSIGNMENTS.ASSIGNEE_LABEL') }}</span>
        </div>
        <div
          v-for="(assignment, index) in assignments"
          :key="assignment.id || `new-${index}`"
          class="grid grid-cols-1 md:grid-cols-[260px_200px_260px_auto] gap-3 items-center"
        >
          <CompanySelect
            v-model="assignment.companyId"
            :selected-name="assignment.companyName"
            :show-none-option="false"
          />
          <ComboBox
            :model-value="assignment.target"
            :options="targetOptions"
            @update:model-value="value => onTargetChange(assignment, value)"
          />
          <ComboBox
            v-model="assignment.assigneeId"
            :options="assigneeOptions(assignment.target)"
            :placeholder="t('COMPANY_ASSIGNMENTS.CHOOSE_ASSIGNEE')"
          />
          <Button
            v-tooltip.top="t('COMPANY_ASSIGNMENTS.REMOVE')"
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="removeRow(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
